import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Space, Popconfirm, message, Row, Col, Collapse, Empty, Image } from 'antd';
import { PlusOutlined, EditOutlined, CopyOutlined, DeleteOutlined, SaveOutlined, CloseOutlined, CarOutlined, PictureOutlined } from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../utils/auth';
import RichTextEditor from './RichTextEditor';
import ProductImageUpload from './ProductImageUpload';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

const MealTag = ({ icon, label, value }) => {
  if (!value) return null;
  return <span style={{ fontSize: 13, color: '#666', marginRight: 12 }}>{icon} {label}: {value}</span>;
};

const DayPanel = ({ day, onSave, onDelete, onDuplicate }) => {
  const [form, setForm] = useState({ ...day });
  const [saving, setSaving] = useState(false);
  const saveTimerRef = React.useRef(null);

  React.useEffect(() => { setForm({ ...day }); }, [day]);

  const updateField = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      await onSave(day.id, updated);
      setSaving(false);
    }, 1500);
  };

  return (
    <div>
      {saving && <div style={{ color: '#1890ff', fontSize: 12, marginBottom: 8 }}>自动保存中...</div>}
      <Row gutter={12}>
        <Col span={10}>
          <Input value={form.title} onChange={e => updateField('title', e.target.value)} addonBefore="标题" style={{ marginBottom: 8 }} />
        </Col>
        <Col span={7}>
          <Input value={form.route} onChange={e => updateField('route', e.target.value)} addonBefore="路线" style={{ marginBottom: 8 }} />
        </Col>
        <Col span={7}>
          <Input value={form.drivingInfo} onChange={e => updateField('drivingInfo', e.target.value)} addonBefore="车程" style={{ marginBottom: 8 }} />
        </Col>
      </Row>
      <Row gutter={12} style={{ marginBottom: 8 }}>
        <Col span={6}><Input value={form.breakfast} onChange={e => updateField('breakfast', e.target.value)} addonBefore="早餐" /></Col>
        <Col span={6}><Input value={form.lunch} onChange={e => updateField('lunch', e.target.value)} addonBefore="午餐" /></Col>
        <Col span={6}><Input value={form.dinner} onChange={e => updateField('dinner', e.target.value)} addonBefore="晚餐" /></Col>
        <Col span={6}><Input value={form.accommodation} onChange={e => updateField('accommodation', e.target.value)} addonBefore="住宿" /></Col>
      </Row>
      <div style={{ marginBottom: 4, fontSize: 13, color: '#666' }}>行程描述</div>
      <RichTextEditor value={form.description} onChange={v => updateField('description', v)} placeholder="当天详细行程描述..." style={{ minHeight: 200 }} />
      <div style={{ marginTop: 12, marginBottom: 4, fontSize: 13, color: '#666' }}><PictureOutlined /> 行程图片</div>
      <ProductImageUpload value={form.images || []} onChange={v => updateField('images', v)} mode="multiple" />
    </div>
  );
};

const ProductDayEditor = ({ destinationId }) => {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  const fetchDays = async () => {
    if (!destinationId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/product-days/destination/${destinationId}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success) setDays(res.data.data);
    } catch (err) {
      message.error('获取行程失败');
    }
    setLoading(false);
  };

  useEffect(() => { fetchDays(); }, [destinationId]);

  const handleAdd = async () => {
    setAdding(true);
    try {
      const nextDay = days.length + 1;
      await axios.post(`${API_BASE}/product-days`, {
        destinationId,
        title: `第${nextDay}天行程`,
        dayNumber: nextDay
      }, { headers: AuthService.getAuthHeaders() });
      fetchDays();
    } catch (err) {
      message.error('添加失败');
    }
    setAdding(false);
  };

  const handleSave = async (id, data) => {
    try {
      await axios.put(`${API_BASE}/product-days/${id}`, data, {
        headers: AuthService.getAuthHeaders()
      });
      message.success('已保存');
      fetchDays();
    } catch (err) {
      message.error('保存失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE}/product-days/${id}`, {
        headers: AuthService.getAuthHeaders()
      });
      message.success('已删除');
      fetchDays();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API_BASE}/product-days/${id}/duplicate`, {}, {
        headers: AuthService.getAuthHeaders()
      });
      message.success('复制成功');
      fetchDays();
    } catch (err) {
      message.error('复制失败');
    }
  };

  if (!destinationId) {
    return <Empty description="请先保存产品基本信息" />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ color: '#666' }}>共 {days.length} 天行程，点击卡片编辑</span>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} loading={adding}>
          添加一天
        </Button>
      </div>

      {days.length === 0 && !loading && (
        <Empty description='暂无行程，点击「添加一天」开始编辑' style={{ padding: 40 }} />
      )}

      {days.length > 0 && (
        <Collapse
          accordion
          items={days.map(day => ({
            key: String(day.id),
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                <span style={{ fontWeight: 600, color: '#1890ff' }}>Day {day.dayNumber}</span>
                <span style={{ fontWeight: 500 }}>{day.title}</span>
                {day.route && <span style={{ color: '#999', fontSize: 13 }}>({day.route})</span>}
                <span style={{ flex: 1 }} />
                <span onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 4 }}>
                  <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => handleDuplicate(day.id)} />
                  <Popconfirm title="确认删除？" onConfirm={() => handleDelete(day.id)}>
                    <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                  </Popconfirm>
                </span>
              </div>
            ),
            children: <DayPanel day={day} onSave={handleSave} onDelete={handleDelete} onDuplicate={handleDuplicate} />
          }))}
          bordered={false}
          style={{ background: 'transparent' }}
        />
      )}
    </div>
  );
};

export default ProductDayEditor;
